import {useState} from "react";
import {useForm, Controller} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {App as AntdApp, Alert, Input, Modal, Table, Tag, Tooltip} from "antd";
import {Button, FontAwesomeIcon, ModalForm} from "~/components";
import {InputField, NumberField} from "~/components/Forms";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {
	useGetApiKeysQuery,
	useCreateApiKeyMutation,
	useRevokeApiKeyMutation,
} from "~/reduxs/api/apiAccessApiSlice";

const schema = Yup.object().shape({
	name: Yup.string().required('Vui lòng đặt tên cho key (vd: Server production)'),
});

/**
 * Tab "API Keys" — credential dài hạn gọi API public v1 (header X-API-Key).
 * Key đầy đủ CHỈ hiện đúng 1 lần ngay sau khi tạo (BE chỉ lưu hash) — modal copy.
 */
function ApiKeysPanel({canApi}) {

	const {notification, modal} = AntdApp.useApp();

	const {data: keys = [], isLoading} = useGetApiKeysQuery();
	const [createApiKey, {isLoading: creating}] = useCreateApiKeyMutation();
	const [revokeApiKey] = useRevokeApiKeyMutation();

	const [formOpen, setFormOpen] = useState(false);
	// Key vừa tạo (hiện 1 lần): {key, name}
	const [createdKey, setCreatedKey] = useState(null);

	const {control, handleSubmit, reset, formState: {errors}} = useForm({
		defaultValues: {name: '', expires_days: ''},
		resolver: yupResolver(schema),
	});

	const openForm = () => {
		reset({name: '', expires_days: ''});
		setFormOpen(true);
	};

	const onSubmit = async (values) => {
		try {
			const body = {name: values.name};
			if (values.expires_days) body.expires_days = Number(values.expires_days);
			const result = await createApiKey(body).unwrap();
			setFormOpen(false);
			setCreatedKey(result);
		} catch (e) {
			notification.error({message: 'Không tạo được API key', description: rtkErrorMessage(e)});
		}
	};

	const handleRevoke = (row) => {
		modal.confirm({
			title: 'Thu hồi API key',
			content: `Thu hồi key "${row.name}"? Mọi tích hợp đang dùng key này sẽ ngừng hoạt động ngay.`,
			okText: 'Thu hồi',
			okButtonProps: {danger: true},
			cancelText: 'Hủy',
			onOk: async () => {
				try {
					await revokeApiKey(row.id).unwrap();
					notification.success({message: 'Đã thu hồi API key'});
				} catch (e) {
					notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Thu hồi thất bại')});
				}
			},
		});
	};

	const copyKey = async () => {
		try {
			await navigator.clipboard.writeText(createdKey.key);
			notification.success({message: 'Đã copy API key'});
		} catch (e) {
			notification.error({message: 'Không copy được — hãy bôi đen và copy thủ công'});
		}
	};

	const columns = [
		{title: 'Tên', dataIndex: 'name', key: 'name'},
		{title: 'Key', dataIndex: 'key_hint', key: 'key_hint', width: 130, render: (v) => <code>{v}</code>},
		{
			title: 'Trạng thái', dataIndex: 'status', key: 'status', width: 120,
			render: (s) => s === 'active' ? <Tag color="green">Hoạt động</Tag> : <Tag color="red">Đã thu hồi</Tag>,
		},
		{title: 'Hết hạn', dataIndex: 'expires_at', key: 'expires_at', width: 170, responsive: ['md'], render: (v) => v || 'Không'},
		{title: 'Tạo lúc', dataIndex: 'created', key: 'created', width: 170, responsive: ['md']},
		{
			title: '', key: 'actions', width: 70,
			render: (_, r) => r.status === 'active' && (
				<Tooltip title="Thu hồi key">
					<Button small red onClick={() => handleRevoke(r)}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-ban" />} />
				</Tooltip>
			),
		},
	];

	return (
		<div>
			<div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: 14}}>
				<Tooltip title={!canApi ? 'Gói hiện tại không hỗ trợ API hệ thống' : ''}>
					<span>
						<Button primary disabled={!canApi} onClick={openForm}
							leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}>
							Tạo API key
						</Button>
					</span>
				</Tooltip>
			</div>

			<Table
				rowKey="id"
				columns={columns}
				dataSource={keys}
				loading={isLoading}
				locale={{emptyText: 'Chưa có API key nào — tạo key để gọi API public v1'}}
				pagination={{pageSize: 10, hideOnSinglePage: true}}
				scroll={{x: 'max-content'}}
			/>

			<ModalForm
				open={formOpen}
				icon="fa-light fa-key"
				title="Tạo API key"
				subtitle="Dùng ở header X-API-Key khi gọi API public v1"
				onCancel={() => setFormOpen(false)}
				onOk={handleSubmit(onSubmit)}
				okText="Tạo key"
				loading={creating}
			>
				<Controller control={control} name="name" render={({field}) => (
					<InputField label="Tên key" placeholder="VD: Server production" errors={errors} {...field} />
				)} />
				<Controller control={control} name="expires_days" render={({field}) => (
					<NumberField label="Hết hạn sau (ngày) — bỏ trống nếu không hết hạn" placeholder="VD: 90" errors={errors} {...field} />
				)} />
			</ModalForm>

			<Modal
				open={!!createdKey}
				onCancel={() => setCreatedKey(null)}
				footer={null}
				width={620}
				title="API key đã tạo — copy ngay"
			>
				<Alert
					type="warning"
					showIcon
					style={{marginBottom: 12}}
					message="Key đầy đủ chỉ hiện đúng MỘT lần"
					description="Hệ thống chỉ lưu bản băm của key. Đóng cửa sổ này là không xem lại được — hãy copy và cất vào nơi an toàn."
				/>
				<div style={{display: 'flex', gap: 8}}>
					<Input readOnly value={createdKey?.key || ''} style={{fontFamily: 'monospace'}} />
					<Button primary onClick={copyKey} leftIcon={<FontAwesomeIcon icon="fa-light fa-copy" />}>Copy</Button>
				</div>
			</Modal>
		</div>
	);
}

export default ApiKeysPanel;
